// Phase NAATI-MAX B2 — /en/translate/naati/levels
import { createFileRoute } from "@tanstack/react-router";
import NaatiKnowledgePage from "@/components/NaatiKnowledgePage";
import { NAATI_LEVELS, NAATI_LEVELS_TRANSLATOR, NAATI_LEVELS_INTERPRETER } from "@/data/naati-certification-levels";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/en/translate/naati/levels`;

const faqs = [
  { q: "What is the difference between CT and CAT?", a: "CT (Certified Translator) is the standard accreditation used for ~90% of civil documents submitted to Australian authorities. CAT (Certified Advanced Translator) is a higher level passed via a specialist test — used for medical manuals, court judgments, and complex international contracts." },
  { q: "Does the NAATI stamp expire?", a: "Yes. NAATI requires every translator to recertify every 3 years by submitting CPD (Continuing Professional Development) records and work practice evidence. Stamps from expired translators are not accepted by DHA." },
  { q: "How do CPI / CI / CSHI differ?", a: "CPI is an entry-level community interpreter. CI is the standard for government and business work. CSHI/CSLI are specialist interpreters required for Australian public hospitals and courts." },
  { q: "What is the highest NAATI level?", a: "CCI — Certified Conference Interpreter — is the apex NAATI credential, used at APEC and ASEAN heads-of-state meetings, UN General Assembly sessions, the Australian Parliament, and Federal Court appellate hearings. It requires passing booth-based simultaneous interpreting under NAATI examiners plus a minimum of 750 logged conference hours; fewer than 40 Thai↔English CCIs practice worldwide." },
];

export const Route = createFileRoute("/en/translate/naati/levels")({
  head: () => ({
    meta: [
      { title: ctaTitle("NAATI Certification Levels — CT, CAT, CPI, CI, CSHI | NYC", "/en/translate/naati/levels", "en")},
      { name: "description", content: ctaDesc("Complete guide to NAATI certification levels for translators (CT/CAT/RPT) and interpreters. Ask us anything — real experts reply within minutes.", "/en/translate/naati/levels", "en")},
      { property: "og:title", content: ctaTitle("NAATI Certification Levels — CT, CAT, CPI, CI, CSHI, CSLI, CCI", "/en/translate/naati/levels", "en")},
      { property: "og:description", content: ctaDesc("Full ladder of NAATI accreditations with example documents and assignment scope.", "/en/translate/naati/levels", "en")},
      { property: "og:url", content: URL },
    ],
    links: canonicalAndHreflang("/translate/naati/levels", "/en/translate/naati/levels"),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        "@id": `${URL}#article`, headline: "NAATI Certification Levels Explained", inLanguage: "en",
        author: { "@type": "Organization", name: "NYC Language Institute" },
        publisher: { "@type": "Organization", name: "NYC Language Institute" },
        datePublished: "2026-06-27", dateModified: "2026-06-27", url: URL,
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }) },
      { type: "application/ld+json", children: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage", "@id": `${URL}#speakable`, url: URL, name: "NAATI Certified Translation",
        speakable: { "@type": "SpeakableSpecification", cssSelector: ["[data-speakable]", "h1", ".direct-answer"] },
      }) },
    ],
  }),
  component: () => (
    <NaatiKnowledgePage
      locale="en"
      eyebrow="Knowledge Base"
      h1="Every NAATI Certification Level Explained — Pick the Right One"
      lead="NAATI (Australia's National Accreditation Authority for Translators and Interpreters) defines 8 levels: 3 for translators (CT/CAT/RPT) and 5 for interpreters (CPI/CI/CSHI/CSLI/CCI). Picking the wrong level wastes money or gets your file rejected."
      speakableAnswer="CT covers about 90% of civil documents lodged with Australian authorities. CAT is for complex specialist material. CSHI/CSLI are required for Australian public hospitals and courts. CCI is the top level for national and international conferences."
      sections={[
        {
          heading: "Translators — 3 levels",
          paragraph: "Every NAATI translator carries a unique CPN number printed on the stamp and must recertify every 3 years.",
          table: {
            headers: ["Level", "Scope", "Example documents"],
            rows: NAATI_LEVELS_TRANSLATOR.map((l) => [`${l.code}\n${l.nameEn}`, l.scopeEn, l.exampleDocsEn.slice(0, 3).join(" • ")]),
          },
        },
        {
          heading: "Interpreters — 5 levels",
          paragraph: "Australian courts and public hospitals require at least CSHI or CSLI.",
          table: {
            headers: ["Level", "Scope", "Example assignments"],
            rows: NAATI_LEVELS_INTERPRETER.map((l) => [`${l.code}\n${l.nameEn}`, l.scopeEn, l.exampleDocsEn.slice(0, 3).join(" • ")]),
          },
        },
        {
          heading: "Testing & Recertification",
          bullets: NAATI_LEVELS.map((l) => `${l.code} — ${l.testedEn}`),
        },
      ]}
      faqs={faqs}
      cta={{ label: "Get advice on which level to use", href: "/en/translate/naati" }}
    />
  ),
});
